import { Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChildren } from "@angular/core";
import { ChatAudit } from "@common/ADAPT.Common.Model/organisation/chat-audit";
import { ElementUtilities } from "@common/lib/utilities/element-utilities";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { IAdaptMenuItem } from "@common/ux/menu/menu.component";
import { ItemClickEvent } from "devextreme/ui/list";
import { HidingEvent, ShowingEvent } from "devextreme/ui/popover";
import { DxPopoverComponent } from "devextreme-angular";
import { BehaviorSubject, debounceTime, finalize, switchMap, tap } from "rxjs";
import { AiPricingService } from "../ai-pricing.service";
import { ChatAiUiService, IChatHistoryEntry } from "../chat-ai-ui.service";

interface IGroupedConversations {
    key: string;
    items: ChatAudit[];
}

@Component({
    selector: "adapt-conversation-list",
    standalone: false,
    templateUrl: "./conversation-list.component.html",
    styleUrl: "./conversation-list.component.css",
})
export class ConversationListComponent extends BaseComponent {
    @Input() public currentConversationId?: string;
    @Input() public swatchTheme?: string;
    @Output() public conversationSwitched = new EventEmitter<{ conversationId: string, history: IChatHistoryEntry[], totalCost: number }>();
    @Output() public conversationDeleted = new EventEmitter<string>();
    public groupedConversations: IGroupedConversations[] = [];
    public loading = false;
    private activePopoverId: string | null = null;
    private activePopoverInstance: DxPopoverComponent | null = null;
    private readonly menuItemCache = new Map<string, IAdaptMenuItem[]>();

    @ViewChildren("conversationMenuPopover") private popoverRefs?: QueryList<DxPopoverComponent>;

    private triggerUpdate$ = new BehaviorSubject<boolean>(false);
    private updatedSince?: Date;

    public constructor(
        elementRef: ElementRef,
        private chatService: ChatAiUiService,
        private pricingService: AiPricingService,
        private dialogService: AdaptCommonDialogService,
    ) {
        super(elementRef);

        this.triggerUpdate$.pipe(
            tap((showLoading) => {
                if (showLoading) {
                    this.loading = true;
                }
            }),
            debounceTime(500),
            switchMap(() => this.internalUpdateConversations()),
            this.takeUntilDestroyed(),
            finalize(() => this.loading = false),
        ).subscribe(() => this.isInitialised = true);
    }

    private internalUpdateConversations() {
        // nimbus doesn't have signalr
        // - so force remote every time this component becomes visible (only updated entities will be fetched)
        const fetchSince = this.updatedSince;
        // update before fetch to ensure we don't miss any updates while waiting for response
        this.updatedSince = new Date();
        return this.chatService.getConversations(fetchSince, true).pipe(
            tap((conversations) => this.groupedConversations = this.groupConversationsByDay(conversations)),
        );
    }

    public refreshConversations() {
        this.triggerUpdate$.next(false);
    }

    public formatConversationTime(timestamp: Date) {
        if (Number.isNaN(timestamp.getTime())) {
            return "Unknown time";
        }

        return timestamp.toLocaleTimeString(undefined, {
            hour: "numeric",
            minute: "2-digit",
        });
    }

    public switchConversation(e: ItemClickEvent<ChatAudit>) {
        this.switchConversationInternal(e.itemData);
    }

    public getItemMenu(item: ChatAudit): IAdaptMenuItem[] {
        const cached = this.menuItemCache.get(item.conversationId);
        if (cached) {
            return cached;
        }

        const menuItem: IAdaptMenuItem[] = [
            {
                text: "Rename",
                icon: "fal fa-fw fa-text-width",
                onClick: () => {
                    this.activePopoverInstance?.instance.hide();
                    this.renameConversation(item);
                },
            },
            {
                text: "Delete conversation",
                icon: "fal fa-fw fa-trash",
                visible: item.conversationId !== this.currentConversationId,
                onClick: () => {
                    this.activePopoverInstance?.instance.hide();
                    this.deleteConversation(item);
                },
            },
        ];

        this.menuItemCache.set(item.conversationId, menuItem);
        return menuItem;
    }

    public renameConversation(item: ChatAudit) {
        this.chatService.renameConversation(item).pipe(
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    public deleteConversation(item: ChatAudit) {
        if (this.currentConversationId === item.conversationId) {
            return;
        }

        this.loading = true;
        this.dialogService.openConfirmationDialog({
            title: "Delete conversation",
            message: `Are you sure you want to delete the conversation with the following subject?<br><br>${item.subject}`,
            confirmButtonPreset: "deleteAndSave",
        }).pipe(
            switchMap(() => this.chatService.deleteConversation(item.conversationId)),
            switchMap(() => this.internalUpdateConversations()),
            tap(() => this.conversationDeleted.emit(item.conversationId)),
            this.takeUntilDestroyed(),
            finalize(() => this.loading = false),
        ).subscribe();
    }

    public onConversationPopoverShowing(e: ShowingEvent, id: string) {
        if (this.activePopoverId && this.activePopoverId !== id) {
            this.activePopoverInstance?.instance.hide();
        }
        this.activePopoverId = id;
        this.activePopoverInstance = this.popoverRefs?.find((p) => p.instance === e.component) ?? null;

        // clear cache so menu items are rebuilt with current disabled state
        this.menuItemCache.delete(id);
    }

    public onConversationPopoverHiding(e: HidingEvent, id: string) {
        if (!e.cancel && this.activePopoverId === id) {
            this.activePopoverId = null;
            this.activePopoverInstance = null;
        }
    }

    public toggleConversationPopover(event: Event, conversationId: string) {
        this.purgeDetachedPopovers();

        const popover = this.popoverRefs?.find((p) => p.instance.$element().attr("data-conversation-id") === conversationId) ?? null;
        if (popover) {
            const isVisible = popover.instance.option("visible");
            if (isVisible) {
                popover.instance.hide();
            } else {
                popover.instance.show();
            }
        }

        event.stopPropagation();
    }

    private purgeDetachedPopovers() {
        if (!this.popoverRefs) {
            return;
        }

        const activePopovers = this.popoverRefs.toArray().filter((p) => {
            if (ElementUtilities.isDetached(p.instance.option("target") as HTMLElement)) {
                const element = p.instance.$element();
                p.instance.dispose();
                element.remove();
                return false;
            }
            return true;
        });

        if (activePopovers.length !== this.popoverRefs.length) {
            // purge detached from query list so it doesn't keep growing with stale references
            this.popoverRefs.reset(activePopovers);
            this.popoverRefs.notifyOnChanges();
        }
    }

    private switchConversationInternal(chatAudit?: ChatAudit) {
        if (!chatAudit || this.currentConversationId === chatAudit.conversationId) {
            return;
        }

        this.loading = true;
        this.chatService.getConversationHistory(chatAudit.conversationId).pipe(
            this.takeUntilDestroyed(),
            finalize(() => this.loading = false),
        ).subscribe((history) => {
            const totalCost = this.pricingService.calculateTotalCost(chatAudit);
            this.conversationSwitched.emit({ conversationId: chatAudit.conversationId, history, totalCost });
        });
    }

    private groupConversationsByDay(conversations: ChatAudit[]): IGroupedConversations[] {
        // conversations are already sorted by end time desc, so we can iterate through them and group them by day
        const groups: IGroupedConversations[] = [];
        for (const conversation of conversations) {
            const groupKey = this.getConversationDayLabel(conversation.endTime);
            const currentGroup = groups.at(-1); // last element
            if (!currentGroup || currentGroup.key !== groupKey) {
                groups.push({
                    key: groupKey,
                    items: [conversation],
                });
            } else {
                currentGroup.items.push(conversation);
            }

            // grouped dxList inserted this - if not updated when values changed, dxList is messed up.
            (conversation as any).key = groupKey;
        }

        return groups;
    }

    private getConversationDayLabel(timestamp: Date): string {
        if (Number.isNaN(timestamp.getTime())) {
            return "Unknown date";
        }

        const today = new Date();
        const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const startOfTimestamp = new Date(timestamp.getFullYear(), timestamp.getMonth(), timestamp.getDate());
        const diffDays = Math.floor((startOfToday.getTime() - startOfTimestamp.getTime()) / (24 * 60 * 60 * 1000));

        if (diffDays === 0) {
            return "Today";
        }

        if (diffDays === 1) {
            return "Yesterday";
        }

        const options: Intl.DateTimeFormatOptions = {
            weekday: "long",
            month: "short",
            day: "numeric",
        };

        if (timestamp.getFullYear() !== today.getFullYear()) {
            options.year = "numeric";
        }

        return timestamp.toLocaleDateString(undefined, options);
    }
}
